import React, { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Plus,
  Trash2,
  Eye,
  AlertCircle,
  ShieldBan,
  Flag,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useTRPC } from "@/trpc";
import {
  JobPostRequirementType,
  JobPostRequirementOperator,
  JobPostRequirementSeverity,
} from "@api/shared-runtime/enums";
import { allowedOperatorsByRequirementType } from "@api/shared-runtime/job-post-requirements";
import { USStateSelect } from "@/components/Selects/USStateSelect";
import {
  RequirementRow,
  RequirementValue,
  defaultValueForType,
  defaultOperatorForType,
  makeRequirementKey,
  requirementRowIsValid,
} from "../utils/job-data";

// Re-export the type so consumers can import from one place
export type { RequirementRow };

// ─── Constants ────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<JobPostRequirementType, string> = {
  [JobPostRequirementType.FreeText]: "Description (free text)",
  [JobPostRequirementType.StateLicense]: "State License",
  [JobPostRequirementType.ProfessionalType]: "Professional Type",
  [JobPostRequirementType.Specialty]: "Specialty",
};

// FreeText first, then structured types. ProfessionalType and Specialty
// rows are configured via the top-of-form match-strictness radios on the
// job-post form — they're stripped from the editor's `value` before render
// (see forms/job-post.tsx load chain), so the editor only handles these two.
const TYPE_ORDER: JobPostRequirementType[] = [
  JobPostRequirementType.FreeText,
  JobPostRequirementType.StateLicense,
];

const OPERATOR_LABELS: Record<JobPostRequirementOperator, string> = {
  [JobPostRequirementOperator.HasAny]: "has any of",
  [JobPostRequirementOperator.HasAll]: "has all of",
  [JobPostRequirementOperator.Matches]: "matches",
};

const SEVERITY_CONFIG: Record<
  JobPostRequirementSeverity,
  {
    label: string;
    description: string;
    icon: React.ReactNode;
    badgeClass: string;
  }
> = {
  [JobPostRequirementSeverity.Block]: {
    label: "Block",
    description:
      "Provider cannot apply — shown an error before they can express interest.",
    icon: <ShieldBan className="h-4 w-4 text-red-500" />,
    badgeClass: "border-red-200 bg-red-50 text-red-700",
  },
  [JobPostRequirementSeverity.Flag]: {
    label: "Flag for review",
    description:
      "Provider proceeds normally, but the org sees a flag for manual review.",
    icon: <Flag className="h-4 w-4 text-amber-500" />,
    badgeClass: "border-amber-200 bg-amber-50 text-amber-700",
  },
};

// ─── Value Editors ────────────────────────────────────────────────────────────

function StateLicenseValueEditor({
  value,
  onChange,
  inputId,
}: {
  value: Extract<RequirementValue, { kind: "state_license" }>;
  onChange: (v: Extract<RequirementValue, { kind: "state_license" }>) => void;
  inputId: string;
}) {
  return (
    <USStateSelect
      inputId={inputId}
      multiple
      value={value.states}
      onChange={(states) =>
        onChange({
          kind: "state_license",
          states: Array.isArray(states) ? states : [states],
        })
      }
      placeholder="Select states..."
    />
  );
}

// ─── Single Row Editor ────────────────────────────────────────────────────────

function RequirementRowEditor({
  row,
  index,
  onChange,
  onRemove,
}: {
  row: RequirementRow;
  index: number;
  onChange: (updated: RequirementRow) => void;
  onRemove: () => void;
}) {
  const fieldId = React.useId();
  const isFreeText = row.type === JobPostRequirementType.FreeText;

  const handleTypeChange = (type: JobPostRequirementType) => {
    onChange({
      ...row,
      type,
      operator: defaultOperatorForType(type),
      value: defaultValueForType(type),
      displayLabel: "",
      severity:
        type === JobPostRequirementType.FreeText
          ? JobPostRequirementSeverity.Flag
          : row.severity,
    });
  };

  // Free-text rows render a compact single-input card
  if (isFreeText) {
    const ftValue = row.value as Extract<
      RequirementValue,
      { kind: "free_text" }
    >;
    return (
      <Card className="rounded-2xl border border-slate-200 bg-white shadow-xs">
        <CardContent className="p-4">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
            <Input
              className="col-span-2 min-w-0"
              aria-label={`Requirement ${index + 1} description`}
              value={ftValue.text}
              onChange={(e) => {
                const text = e.target.value.slice(0, 500);
                onChange({
                  ...row,
                  displayLabel: text,
                  value: { kind: "free_text", text },
                });
              }}
              placeholder="e.g. Active CA license required — describe the requirement..."
              maxLength={500}
            />
            <Select
              value={row.type}
              onValueChange={(v) =>
                handleTypeChange(v as JobPostRequirementType)
              }
            >
              <SelectTrigger
                className="w-full min-w-0"
                aria-label={`Requirement ${index + 1} type`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_ORDER.map((k) => (
                  <SelectItem key={k} value={k}>
                    {TYPE_LABELS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="size-12 shrink-0 rounded-xl p-0 text-slate-500 hover:bg-red-50 hover:text-red-700"
              onClick={onRemove}
              aria-label="Remove requirement"
            >
              <Trash2 size={14} />
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Shown to candidates and used for AI matching. Not enforced by the
            system.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Structured row — full editor
  const severityConfig = SEVERITY_CONFIG[row.severity];

  return (
    <Card className="rounded-2xl border border-slate-200 bg-white shadow-xs">
      <CardHeader className="pb-3 pt-4 px-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-500">
            Rule {index + 1}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="size-10 shrink-0 rounded-xl p-0 text-slate-500 hover:bg-red-50 hover:text-red-700"
            onClick={onRemove}
            aria-label="Remove rule"
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5 px-4 pb-4">
        {/* Kind + Operator row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="min-w-0 space-y-2">
            <Label
              htmlFor={`${fieldId}-type`}
              className="mb-2 block text-sm font-medium text-slate-700"
            >
              Type
            </Label>
            <Select
              value={row.type}
              onValueChange={(v) =>
                handleTypeChange(v as JobPostRequirementType)
              }
            >
              <SelectTrigger id={`${fieldId}-type`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_ORDER.map((k) => (
                  <SelectItem key={k} value={k}>
                    {TYPE_LABELS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {allowedOperatorsByRequirementType[row.type].length > 1 && (
            <div className="min-w-0 space-y-2">
              <Label
                htmlFor={`${fieldId}-operator`}
                className="mb-2 block text-sm font-medium text-slate-700"
              >
                Operator
              </Label>
              <Select
                value={row.operator}
                onValueChange={(v) =>
                  onChange({
                    ...row,
                    operator: v as JobPostRequirementOperator,
                  })
                }
              >
                <SelectTrigger id={`${fieldId}-operator`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allowedOperatorsByRequirementType[row.type].map((op) => (
                    <SelectItem key={op} value={op}>
                      {OPERATOR_LABELS[op]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Value editor */}
        <div>
          <Label
            htmlFor={`${fieldId}-values`}
            className="mb-2 block text-sm font-medium text-slate-700"
          >
            Values
          </Label>
          {row.type === JobPostRequirementType.StateLicense && (
            <StateLicenseValueEditor
              inputId={`${fieldId}-values`}
              value={
                row.value as Extract<
                  RequirementValue,
                  { kind: "state_license" }
                >
              }
              onChange={(v) => onChange({ ...row, value: v })}
            />
          )}
        </div>

        {/* Severity */}
        <div>
          <Label className="mb-2 block text-sm font-medium text-slate-700">
            Severity
          </Label>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(SEVERITY_CONFIG) as JobPostRequirementSeverity[]).map(
              (sev) => {
                const cfg = SEVERITY_CONFIG[sev];
                const isSelected = row.severity === sev;
                return (
                  <button
                    key={sev}
                    type="button"
                    onClick={() => onChange({ ...row, severity: sev })}
                    aria-pressed={isSelected}
                    className={`flex min-h-12 items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                      isSelected
                        ? cfg.badgeClass
                        : "border-slate-400 bg-white text-slate-700 hover:border-slate-500 hover:bg-slate-50"
                    }`}
                  >
                    {cfg.icon}
                    {cfg.label}
                  </button>
                );
              },
            )}
          </div>
          <p className="mt-1.5 text-xs text-slate-500">
            {severityConfig.description}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Preview Panel ────────────────────────────────────────────────────────────

function RequirementsPreviewPanel({
  jobPostId,
  organizationBusinessUnitId,
  interestedCandidateUserIds,
}: {
  jobPostId: string;
  organizationBusinessUnitId: string;
  interestedCandidateUserIds: string[];
}) {
  const trpc = useTRPC();
  const {
    data: preview,
    isLoading,
    error,
  } = useQuery({
    ...trpc.jobPost.previewRequirementsForUsers.queryOptions({
      organizationBusinessUnitId,
      jobPostId,
      userIds: interestedCandidateUserIds,
    }),
    enabled: interestedCandidateUserIds.length > 0,
  });

  if (interestedCandidateUserIds.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        No interested candidates to preview against.
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Calculating impact...
      </div>
    );
  }

  if (error || !preview) {
    return <p className="text-sm text-red-500">Failed to load preview.</p>;
  }

  const blocked = preview.filter((r) => r.blocked).length;
  const flagged = preview.filter(
    (r) => !r.blocked && r.flags.length > 0,
  ).length;
  const passing = preview.length - blocked;

  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-500">
        Of {preview.length} interested candidates with the current rules:
      </p>
      <div className="flex flex-wrap gap-2">
        <Badge
          variant="outline"
          className="border-red-200 bg-red-50 text-red-700"
        >
          <ShieldBan className="h-3 w-3 mr-1" />
          {blocked} blocked
        </Badge>
        <Badge
          variant="outline"
          className="border-amber-200 bg-amber-50 text-amber-700"
        >
          <Flag className="h-3 w-3 mr-1" />
          {flagged} flagged
        </Badge>
        <Badge
          variant="outline"
          className="border-green-200 bg-green-50 text-green-700"
        >
          {passing} passing
        </Badge>
      </div>
      {blocked > 0 && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800 text-xs">
            Saving these rules will immediately affect new applicants. Existing
            decisions are not backfilled.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface JobPostRequirementsEditorProps {
  /** Controlled list of requirement rows */
  value: RequirementRow[];
  /** Called whenever a row is added, removed, or changed */
  onChange: (rows: RequirementRow[]) => void;
  /** When provided, enables the preview-impact panel */
  jobPostId?: string;
  organizationBusinessUnitId?: string;
  interestedCandidateUserIds?: string[];
  /** When true, show a loading skeleton (parent is still fetching initial data) */
  isLoading?: boolean;
}

export function JobPostRequirementsEditor({
  value: rows,
  onChange,
  jobPostId,
  organizationBusinessUnitId,
  interestedCandidateUserIds = [],
  isLoading = false,
}: JobPostRequirementsEditorProps) {
  const [showPreview, setShowPreview] = React.useState(false);

  const handleAddRow = useCallback(() => {
    const newRow: RequirementRow = {
      _key: makeRequirementKey(),
      type: JobPostRequirementType.FreeText,
      operator: JobPostRequirementOperator.Matches,
      severity: JobPostRequirementSeverity.Flag,
      displayLabel: "",
      value: { kind: "free_text", text: "" },
    };
    onChange([...rows, newRow]);
  }, [rows, onChange]);

  const handleRowChange = useCallback(
    (key: string, updated: RequirementRow) => {
      onChange(rows.map((r) => (r._key === key ? updated : r)));
    },
    [rows, onChange],
  );

  const handleRowRemove = useCallback(
    (key: string) => {
      onChange(rows.filter((r) => r._key !== key));
    },
    [rows, onChange],
  );

  const validCount = rows.filter(requirementRowIsValid).length;
  const hasIncomplete = rows.length > 0 && validCount < rows.length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            Requirements
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Add requirements as either free text (shown to candidates, used for
            AI matching) or structured rules (system enforces gates and
            rejection).
          </p>
        </div>
        {jobPostId &&
          organizationBusinessUnitId &&
          interestedCandidateUserIds.length > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowPreview((p) => !p)}
              className="shrink-0 gap-2"
              aria-expanded={showPreview}
            >
              <Eye size={16} aria-hidden="true" />
              {showPreview ? "Hide" : "Preview impact"}
            </Button>
          )}
      </div>

      {showPreview && jobPostId && organizationBusinessUnitId && (
        <div className="rounded-xl border border-primary/15 bg-primary/5 p-4">
          <p className="mb-2 text-sm font-medium text-primary">
            Saved rule impact
          </p>
          <RequirementsPreviewPanel
            jobPostId={jobPostId}
            organizationBusinessUnitId={organizationBusinessUnitId}
            interestedCandidateUserIds={interestedCandidateUserIds}
          />
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading requirements...
        </div>
      ) : (
        <div className="space-y-3">
          {rows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 px-4 py-8 text-center">
              <p className="text-sm text-slate-500">No requirements set.</p>
              <p className="text-xs text-slate-500 mt-1">
                Add free-text descriptions or structured gates below.
              </p>
            </div>
          ) : (
            rows.map((row, index) => (
              <RequirementRowEditor
                key={row._key}
                row={row}
                index={index}
                onChange={(updated) => handleRowChange(row._key, updated)}
                onRemove={() => handleRowRemove(row._key)}
              />
            ))
          )}
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        onClick={handleAddRow}
        className="w-full gap-2 sm:w-auto"
        disabled={rows.length >= 50}
      >
        <Plus size={16} aria-hidden="true" />
        Add Requirement
      </Button>

      {hasIncomplete && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800 text-xs">
            {rows.length - validCount} requirement(s) are incomplete. Fill in
            the text or values before saving.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
